import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { posesData } from '../data/poses';

const { FiArrowLeft, FiCopy, FiCheck, FiCheckSquare, FiSquare } = FiIcons;

const categoryPalette = {
  Business: '#0F0F0F',
  Senior: '#1C1C1C',
  Couple: '#2E2A2B',
  Creative: '#3B2F2F',
  Lifestyle: '#4B3B3F',
  Fitness: '#5C4B51',
  Artistic: '#6D5C5F',
  Branding: '#7E6D6F'
};

function PoseDetail() {
  const { poseId } = useParams();
  const pose = posesData.find(p => p.id === poseId);
  const [copied, setCopied] = React.useState(false);
  const [selected, setSelected] = React.useState(false);

  React.useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('selectedPoses') || '[]');
    setSelected(stored.includes(poseId));
  }, [poseId]);

  if (!pose) {
    return (
      <div className="max-w-5xl mx-auto py-20 text-center">
        <h2 className="text-2xl font-serif text-slate-800 mb-4">Pose not found</h2>
        <Link to="/" className="text-brand-primary hover:underline">Return to collection</Link>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(pose.instruction);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSelection = () => {
    const stored = JSON.parse(localStorage.getItem('selectedPoses') || '[]');
    let updated = selected ? stored.filter(id => id !== poseId) : [...stored, poseId];
    localStorage.setItem('selectedPoses', JSON.stringify(updated));
    setSelected(!selected);
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  return (
    <div className="max-w-5xl mx-auto">
      <header className="mb-10">
        <Link to={`/category/${pose.subject.toLowerCase()}`} className="inline-flex items-center text-slate-400 hover:text-brand-primary transition-colors text-[10px] uppercase tracking-[0.2em] font-bold">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to {pose.subject}
        </Link>
      </header>

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row border border-slate-100">
        <div className="lg:w-3/5 aspect-square lg:aspect-auto" style={{ backgroundColor: categoryPalette[pose.subject] }}>
          <img src={pose.link} className="w-full h-full object-cover" alt={`Pose ${poseId}`} />
        </div>

        <div className="lg:w-2/5 p-12 flex flex-col justify-between bg-white">
          <div>
            <div className="flex items-center justify-between mb-8">
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-bold">{pose.subject} Series</span>
              <button 
                onClick={toggleSelection}
                className={`flex items-center text-[10px] uppercase tracking-widest font-bold transition-colors ${selected ? 'text-brand-primary' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <SafeIcon icon={selected ? FiCheckSquare : FiSquare} className="mr-2 text-lg" />
                {selected ? 'Added' : 'Add'}
              </button>
            </div>

            <h1 className="text-5xl md:text-6xl font-serif mb-10 leading-tight">Pose Ref. {poseId.split('-')[1]}</h1>

            <div className="space-y-10">
              <section>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Direction Script</h3>
                <div className="bg-slate-50 p-8 rounded-2xl relative group border border-slate-100">
                  <p className="text-slate-800 font-serif text-2xl leading-relaxed">"{pose.instruction}"</p>
                  <button 
                    onClick={handleCopy}
                    className="absolute top-4 right-4 p-2 text-slate-300 hover:text-brand-primary transition-colors"
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-lg" />
                  </button>
                </div>
              </section>

              <section>
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Professional Insight</h3>
                <ul className="text-xs text-slate-500 space-y-4 font-light">
                  {pose.insights && pose.insights.length > 0 ? (
                    pose.insights.map((insight, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="text-brand-primary font-bold">0{idx + 1}</span> {insight}
                      </li>
                    ))
                  ) : (
                    <li className="text-slate-300 italic">Technical insights coming soon for this reference.</li>
                  )}
                </ul>
              </section>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-50 text-center">
            <span className="text-[9px] text-slate-300 font-bold tracking-[0.3em] uppercase">Matcha Design • Professional Reference System</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoseDetail;